import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "../components/Logo";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const ADMIN_EMAIL = "soporteclinped@expedienteelectronicoclinped.com";
const UPDATED = "28 de junio de 2026";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-outfit font-bold text-lg text-brand-ink">{title}</h2>
      <div className="text-sm text-brand-ink/80 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background py-10 px-5" data-testid="privacy-policy">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl border border-stone-100 p-8 sm:p-10 space-y-7">
        <button onClick={() => navigate("/")} data-testid="privacy-back-btn"
          className="flex items-center gap-1.5 text-muted-foreground hover:text-brand-ink text-sm font-semibold">
          <ArrowLeft size={16} /> Volver
        </button>

        <div className="flex items-center gap-3">
          <Logo size={38} />
        </div>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-sage/15 flex items-center justify-center">
            <ShieldCheck className="text-brand-sageDark" size={26} />
          </div>
          <div>
            <h1 className="font-outfit font-extrabold text-2xl text-brand-ink">Aviso de Privacidad</h1>
            <p className="text-sm text-muted-foreground">ClinPed · Última actualización: {UPDATED}</p>
          </div>
        </div>

        <Section title="1. Responsable del tratamiento de datos">
          <p>ClinPed es una aplicación de expediente clínico para uso profesional del médico. El responsable
          del tratamiento de los datos personales es el médico titular de la cuenta. Para asuntos relativos a
          esta aplicación, puede contactar al administrador en <b>{ADMIN_EMAIL}</b>.</p>
        </Section>

        <Section title="2. Datos que recopilamos">
          <p>Para prestar el servicio recabamos:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Cuenta del médico:</b> nombre, correo y foto de perfil (vía inicio de sesión con Google), y datos profesionales que usted captura (cédulas, universidad, RFC, domicilio, teléfono, firma electrónica).</li>
            <li><b>Datos clínicos de pacientes</b> que usted registra: nombre, fecha de nacimiento, sexo, somatometría, signos vitales, antecedentes, diagnósticos, tratamientos, recetas, estudios y archivos adjuntos.</li>
            <li><b>Datos de uso y pago:</b> registros de suscripción y transacciones (procesadas por Stripe; no almacenamos los datos completos de tarjetas).</li>
          </ul>
        </Section>

        <Section title="3. Finalidad del tratamiento">
          <p>Los datos se utilizan para: gestionar expedientes clínicos conforme a la NOM-004-SSA3-2012,
          generar recetas/estudios/consentimientos, asistencia clínica con inteligencia artificial,
          administrar el acceso y la suscripción, y dar soporte. No vendemos sus datos ni los de sus pacientes.</p>
        </Section>

        <Section title="4. Encargados y terceros">
          <p>Compartimos datos estrictamente necesarios con proveedores que nos ayudan a operar:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Autenticación:</b> Google (inicio de sesión).</li>
            <li><b>Inteligencia artificial:</b> el texto clínico necesario se procesa mediante proveedores de IA (p. ej. OpenAI) para generar sugerencias; la decisión final siempre es del médico.</li>
            <li><b>Pagos:</b> Stripe procesa los cobros de suscripción.</li>
            <li><b>Almacenamiento:</b> infraestructura de nube para guardar el expediente y los archivos adjuntos de forma cifrada en tránsito.</li>
          </ul>
        </Section>

        <Section title="5. Uso de datos de Google (Google Calendar)">
          <p>ClinPed ofrece una integración <b>opcional</b> con Google Calendar para sincronizar las citas del
          médico. Si usted la activa, solicitamos acceso a su calendario de Google mediante OAuth para
          <b> crear, leer, actualizar y eliminar eventos</b> relacionados con las citas que usted registra en la
          aplicación. Únicamente accedemos a los datos necesarios para esta sincronización.</p>
          <p>El uso y la transferencia de la información recibida de las APIs de Google se apega a la
          <b> Política de Datos de Usuario de los Servicios de la API de Google</b>, incluyendo sus
          requisitos de <b>Uso Limitado (Limited Use)</b>. En concreto: no vendemos estos datos, no los usamos
          con fines de publicidad, y solo los empleamos para prestar y mejorar la función de sincronización de
          citas. Usted puede revocar el acceso en cualquier momento desde la aplicación (Agenda → Desconectar
          Google) o desde la <b>configuración de seguridad de su cuenta de Google</b>.</p>
        </Section>

        <Section title="6. Resguardo de datos de salud">
          <p>Los datos de salud son sensibles. Aplicamos medidas de seguridad administrativas y técnicas
          razonables para protegerlos. El acceso al expediente está restringido a la cuenta del médico
          tratante mediante sesión autenticada.</p>
        </Section>

        <Section title="7. Derechos ARCO">
          <p>Conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (México),
          el titular o su representante puede ejercer los derechos de Acceso, Rectificación, Cancelación y
          Oposición (ARCO), así como revocar el consentimiento, escribiendo a <b>{ADMIN_EMAIL}</b>.</p>
        </Section>

        <Section title="8. Conservación y eliminación">
          <p>Los expedientes se conservan durante el tiempo que la normativa sanitaria aplicable exige. El médico
          puede eliminar expedientes y archivos desde la aplicación; las solicitudes de eliminación de cuenta
          pueden dirigirse al administrador.</p>
        </Section>

        <Section title="9. Menores de edad">
          <p>La aplicación gestiona datos de pacientes pediátricos capturados por el médico bajo su
          responsabilidad profesional y con el consentimiento del padre, madre o tutor cuando corresponde.</p>
        </Section>

        <Section title="10. Seguridad y protección de datos sensibles">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>ClinPed aplica medidas administrativas, técnicas y organizativas razonables para proteger los datos personales, la información clínica y los datos obtenidos mediante las APIs de Google contra acceso, pérdida, alteración, divulgación o uso no autorizado.</li>
            <li>Las comunicaciones con la plataforma se realizan mediante conexiones seguras HTTPS/TLS.</li>
            <li>El acceso a la información requiere autenticación y se limita según las funciones y permisos asignados a cada usuario.</li>
            <li>El acceso administrativo se restringe al personal autorizado que necesita la información para operar, mantener o brindar soporte a la plataforma.</li>
            <li>ClinPed utiliza controles de sesión, registros técnicos y medidas de seguridad para prevenir y detectar accesos no autorizados.</li>
            <li>Se realizan actualizaciones y revisiones periódicas de seguridad.</li>
            <li>Los datos de Google Calendar se obtienen únicamente después del consentimiento expreso del usuario mediante OAuth 2.0 y se utilizan exclusivamente para las funciones de agenda, consulta, creación, modificación y sincronización de eventos.</li>
            <li>ClinPed no vende los datos de usuarios de Google, no los utiliza para publicidad y no los comparte con terceros, salvo con proveedores indispensables para operar el servicio bajo obligaciones de confidencialidad y seguridad, o cuando exista una obligación legal.</li>
            <li>El usuario puede revocar en cualquier momento el acceso de ClinPed desde la configuración de seguridad de su cuenta de Google.</li>
            <li>El uso y transferencia de información recibida desde las APIs de Google se realiza de conformidad con la Google API Services User Data Policy, incluidos sus requisitos de Limited Use.</li>
          </ul>
        </Section>

        <Section title="11. Cambios a este aviso">
          <p>Podremos actualizar este Aviso de Privacidad. La fecha de la última actualización se indica al inicio.
          El uso continuo de la aplicación implica la aceptación de los cambios.</p>
        </Section>

        <Section title="12. Contacto">
          <p>Para cualquier duda sobre privacidad o datos personales: <b>{ADMIN_EMAIL}</b></p>
        </Section>
      </motion.div>
    </div>
  );
}
